import type {SessionActiveClient, ToolDefinition} from '@microsoft/agent-host-protocol'
import type {AhpClient} from '@microsoft/agent-host-protocol/client'

import {InstructionPluginSource, type RuleFile} from './instruction-plugin-source'

export class InstructionPluginRuntime {
  #customizationPublished = false
  #source: InstructionPluginSource | undefined
  #resourceClient: AhpClient | undefined

  setRules(rules: RuleFile[]): boolean {
    const source = (this.#source ??= new InstructionPluginSource())
    if (!source.setRules(rules)) return false
    if (source.hasRules()) this.#customizationPublished = true
    return true
  }

  hasPublishedCustomization(): boolean {
    return this.#customizationPublished
  }

  buildActiveClient({
    clientId,
    supplied,
    contributedTools,
    includeEmpty = false,
  }: {
    clientId: string
    supplied?: SessionActiveClient
    contributedTools: ToolDefinition[]
    includeEmpty?: boolean
  }): SessionActiveClient | undefined {
    const instructionCustomization = this.#source?.getCustomization(clientId)
    if (!supplied && contributedTools.length === 0 && !instructionCustomization && !includeEmpty) return undefined

    const suppliedTools = supplied?.tools ?? []
    const {customizations: suppliedCustomizations = [], ...suppliedActiveClient} = supplied ?? {}
    const customizations = [...suppliedCustomizations, ...(instructionCustomization ? [instructionCustomization] : [])]
    return {
      ...suppliedActiveClient,
      clientId,
      tools: [...suppliedTools, ...contributedTools],
      ...(customizations.length > 0 ? {customizations} : {}),
    }
  }

  registerResourceHandlers(
    client: AhpClient,
    logRequest: (method: 'resourceList' | 'resourceRead', uri: string) => void,
  ): void {
    const source = this.#source
    if (!source?.hasRules() || this.#resourceClient === client) return

    client.setResourceRequestHandlers(source.createResourceRequestHandlers(logRequest))
    this.#resourceClient = client
  }

  resetResourceClient(): void {
    this.#resourceClient = undefined
  }
}
